import { Fragment, useEffect, useMemo, useState } from 'react';
import { setCrumbName } from '@/app/crumb-name';
import { useParams } from 'react-router-dom';
import { Avatar, Button, Checkbox, Segmented, Pagination, EmptyState, BrandLoading } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/shared/ui/components/page-header';
import { useAuthStore } from '@/auth/auth-store';
import { useOrgLocations } from '@/scheduling/services/use-org-locations';
import { usePractitionerById } from '@/settings/services/use-practitioner';
import {
  useProviderFees,
  useArchiveProviderFees,
  useUnarchiveProviderFees,
} from '@/billing/services/use-provider-fees';
import { useExportProviderFeesCsv } from '@/billing/services/use-provider-fees-csv';
import { ProviderFeeAddRow } from '@/billing/components/provider-fees/provider-fee-add-row';
import { ProviderFeeEditRow } from '@/billing/components/provider-fees/provider-fee-edit-row';
import { ImportProviderFeesCsvDialog } from '@/billing/components/provider-fees/import-provider-fees-csv-dialog';
import { formatTerse } from '@/billing/components/provider-fees/provider-fee-draft';
import type { OrgLocation } from '@/auth/types';
import type { ProviderFeeShare, ProviderServiceFee } from '@/shared/types/billing';
import { ChevronLeft, Pencil, Search, Trash2, Upload } from 'lucide-react';

/**
 * Provider Fees — the doctor's OWN per-service fees (PROD-1210).
 *
 * PM parity: `dev-scrips-pm-react/src/billing/pages/provider-fees-detail-page.tsx`.
 * Flutter parity: `provider_fees_list_widget.dart`.
 *
 * Differs from PM in WIRING (the surface is self-scoped in PA):
 *   - `providerId` route param IS the logged-in doctor's `doctorSetupId`.
 *   - The doctor name/photo come from `usePractitionerById` (PA has no
 *     org-wide practitioner roster / picker route), NOT `usePractitioners`.
 *   - Breadcrumb + back go to `/settings` (the Billing tab), since PA has
 *     no `/billing/master-data/provider-fees` picker page to return to.
 *
 * Recomposed on Signal DS: shadcn Avatar/Popover/Tooltip/Skeleton → composed
 * DS atoms + semantic markup; DS `Checkbox`, `Segmented`, `Pagination`,
 * `BreadcrumbNavigation`, `Button`, `Icon`. Row-actions menu → inline icon
 * buttons (DS has no DropdownMenu/Popover — see report DS-GAP). No
 * hardcoded hex.
 */

const PAGE_SIZES = ['10', '20', '50'] as const;
type PageSize = 10 | 20 | 50;

export function ProviderFeesDetailPage() {
  const { providerId } = useParams<{ providerId: string }>();

  // ── Doctor header (self-scoped) ───────────────────────────────────────
  const { data: practitioner } = usePractitionerById(providerId);
  const fullName =
    [practitioner?.name?.firstName, practitioner?.name?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim() || 'Provider';
  const photoUrl = practitioner?.photo ?? undefined;
  // The top bar's last crumb names the page ("Settings › <name>'s Fees"); the page draws no breadcrumb of its own.
  useEffect(() => {
    setCrumbName(practitioner ? `${fullName}'s Fees` : null);
    return () => setCrumbName(null);
  }, [practitioner, fullName]);

  // ── Filters / pagination ──────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState<PageSize>(10);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<ProviderServiceFee | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  // ── Data ──────────────────────────────────────────────────────────────
  const orgId = useAuthStore((s) => s.user?.organizationId);
  const { data: orgLocations = [] } = useOrgLocations(orgId);
  const { data, isLoading, isFetching } = useProviderFees({
    providerId: providerId ?? '',
    showArchived,
    q: search || undefined,
    pageNumber,
    pageSize,
  });
  const archive = useArchiveProviderFees();
  const unarchive = useUnarchiveProviderFees();
  const exportCsv = useExportProviderFeesCsv();
  // LOOP-SAFETY: stable mutate fns.
  const { mutate: archiveFees } = archive;
  const { mutate: unarchiveFees } = unarchive;
  const { mutateAsync: exportFeesCsv } = exportCsv;

  const fees = data?.results ?? [];
  const totalRecords = data?.totalNumberOfRecords ?? 0;
  const totalPages = data?.totalNumberOfPages ?? 1;

  const cancelEditing = () => setEditing(null);

  const toggleRow = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allOnPageSelected =
    fees.length > 0 && fees.every((f) => f.id && selected.has(f.id));
  const toggleSelectAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) fees.forEach((f) => f.id && next.delete(f.id));
      else fees.forEach((f) => f.id && next.add(f.id));
      return next;
    });

  const clearSelection = () => {
    cancelEditing();
    setSelected(new Set());
  };

  const onBulkArchive = () => {
    if (selected.size === 0) return;
    cancelEditing();
    archiveFees(Array.from(selected), { onSuccess: clearSelection });
  };
  const onBulkUnarchive = () => {
    if (selected.size === 0) return;
    cancelEditing();
    unarchiveFees(Array.from(selected), { onSuccess: clearSelection });
  };

  const resetPage = () => setPageNumber(1);

  const onExport = async () => {
    if (!providerId) return;
    try {
      const res = await exportFeesCsv({ providerId });
      if (res?.csvLink) window.open(res.csvLink, '_blank', 'noopener,noreferrer');
    } catch {
      /* Global toast surfaces the failure. */
    }
  };

  const onExportSelected = async () => {
    if (!providerId || selected.size === 0) return;
    cancelEditing();
    try {
      const res = await exportFeesCsv({
        providerId,
        providerServiceFeeIds: Array.from(selected),
      });
      if (res?.csvLink) window.open(res.csvLink, '_blank', 'noopener,noreferrer');
      clearSelection();
    } catch {
      /* Global toast surfaces the failure. */
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-grey-100">
      {/* The one page header (PageHeader, like Practice): the provider's photo + name on the left, Import on the
          right. The breadcrumb and the way back live in the top bar (Settings › Provider fees › <name>'s Fees). */}
      <PageHeader
        leading={<Avatar scale="compact" tone="provider" name={fullName} alt={fullName} src={photoUrl} size={48} />}
        title={<>{fullName}&apos;s Fees</>}
        actions={
          <Button
            variant="primary"
            onClick={() => setImportOpen(true)}
            icon={<Upload className="size-4" aria-label="Import" />}
            iconPosition="leading"
          >
            Import
          </Button>
        }
      />

      {/* Top-of-page CREATE banner */}
      <ProviderFeeAddRow providerId={providerId ?? ''} locations={orgLocations} />

      {/* Toolbar — search + links, OR multi-select action bar */}
      {selected.size === 0 ? (
        <div className="px-8 py-3 flex items-center justify-between gap-4 bg-grey-100">
          <div className="relative w-80">
            <span
              className="absolute left-3 top-1/2 -translate-y-1/2 text-text-hint pointer-events-none"
              aria-hidden
            >
              <Search className="size-4" aria-label="Search" />
            </span>
            <input
              type="text"
              placeholder="Search CPT code or name"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                resetPage();
              }}
              className="w-full h-10 pl-10 pr-10 text-sm bg-surface-card rounded-md placeholder:text-text-hint focus:outline-none focus:ring-2 focus:ring-brand-primary"
              aria-label="Search services"
            />
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  resetPage();
                }}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-text-hint hover:text-text-secondary font-bold"
              >
                ✕
              </button>
            )}
          </div>

          <div className="flex items-center gap-6">
            <button
              type="button"
              onClick={onExport}
              disabled={exportCsv.isPending || !providerId}
              className="text-sm font-medium text-brand-primary hover:underline disabled:opacity-50"
            >
              {exportCsv.isPending ? 'Exporting…' : 'Export To File'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowArchived((v) => !v);
                resetPage();
              }}
              className="text-sm font-medium text-brand-primary hover:underline"
            >
              {showArchived ? 'Hide Archived Services' : 'View Archived Services'}
            </button>
          </div>
        </div>
      ) : (
        <div className="px-8 py-3 flex items-center gap-6 bg-grey-100">
          <span className="text-sm text-text-secondary">{selected.size} selected</span>
          <button
            type="button"
            onClick={clearSelection}
            className="text-sm font-medium text-brand-primary hover:underline"
          >
            Unselect All
          </button>
          {showArchived ? (
            <button
              type="button"
              onClick={onBulkUnarchive}
              disabled={unarchive.isPending}
              className="text-sm font-medium text-brand-primary hover:underline disabled:opacity-50"
            >
              {unarchive.isPending ? 'Unarchiving…' : 'Unarchive'}
            </button>
          ) : (
            <button
              type="button"
              onClick={onBulkArchive}
              disabled={archive.isPending}
              className="text-sm font-medium text-brand-primary hover:underline disabled:opacity-50"
            >
              {archive.isPending ? 'Archiving…' : 'Archive'}
            </button>
          )}
          <button
            type="button"
            onClick={onExportSelected}
            disabled={exportCsv.isPending}
            className="text-sm font-medium text-brand-primary hover:underline disabled:opacity-50"
          >
            {exportCsv.isPending ? 'Exporting…' : 'Export Selected'}
          </button>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 min-h-0 overflow-auto px-8 pb-4 bg-grey-100">
        {isLoading ? (
          <BrandLoading size={48} label="Loading fees…" />
        ) : fees.length === 0 ? (
          <EmptyState
            icon="calculator"
            title={
              search
                ? 'No services match this filter.'
                : showArchived
                  ? 'No archived fees.'
                  : 'No fee overrides configured yet.'
            }
          />
        ) : (
          <ProviderFeesTable
            fees={fees}
            locations={orgLocations}
            selected={selected}
            allOnPageSelected={allOnPageSelected}
            onToggleRow={toggleRow}
            onToggleAll={toggleSelectAll}
            isMutating={archive.isPending || unarchive.isPending}
            onArchive={(id) => archiveFees(id)}
            onUnarchive={(id) => unarchiveFees(id)}
            onEdit={(fee) => setEditing(fee)}
            editingFee={editing}
            onDoneEditing={() => setEditing(null)}
            providerId={providerId ?? ''}
          />
        )}
      </div>

      {/* CSV import dialog */}
      <ImportProviderFeesCsvDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        providerId={providerId ?? ''}
        existingFeesCount={totalRecords}
        onImported={() => setPageNumber(1)}
      />

      {/* Pagination footer */}
      {fees.length > 0 && (
        <div className="px-8 py-3 border-t border-border-default bg-surface-card flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Segmented
              aria-label="Rows per page"
              options={PAGE_SIZES.map((s) => ({ label: s, value: s }))}
              value={String(pageSize)}
              onChange={(v) => {
                setPageSize(Number(v) as PageSize);
                resetPage();
              }}
            />
            <span className="text-sm text-text-muted">
              {(pageNumber - 1) * pageSize + 1}–{(pageNumber - 1) * pageSize + fees.length}{' '}
              of {totalRecords} records
              {isFetching && ' · refreshing…'}
            </span>
          </div>
          <Pagination
            page={pageNumber}
            totalPages={totalPages}
            onChange={setPageNumber}
            size="compact"
          />
        </div>
      )}
    </div>
  );
}

// ─── Table ───────────────────────────────────────────────────────────────

interface TableProps {
  fees: ProviderServiceFee[];
  locations: OrgLocation[];
  selected: Set<string>;
  allOnPageSelected: boolean;
  isMutating: boolean;
  onToggleRow: (id: string) => void;
  onToggleAll: () => void;
  onArchive: (id: string) => void;
  onUnarchive: (id: string) => void;
  onEdit: (fee: ProviderServiceFee) => void;
  editingFee: ProviderServiceFee | null;
  onDoneEditing: () => void;
  providerId: string;
}

function ProviderFeesTable({
  fees,
  locations,
  selected,
  allOnPageSelected,
  isMutating,
  onToggleRow,
  onToggleAll,
  onArchive,
  onUnarchive,
  onEdit,
  editingFee,
  onDoneEditing,
  providerId,
}: TableProps) {
  return (
    <table className="w-full text-sm border-separate border-spacing-0">
      <thead className="text-xs font-medium text-text-muted">
        <tr>
          <Th className="w-8">
            <Checkbox
              checked={allOnPageSelected}
              onCheckedChange={onToggleAll}
              aria-label="Select all on page"
            />
          </Th>
          <Th>Service name</Th>
          <Th className="w-20 text-right">WTAX, %</Th>
          {locations.map((loc) => (
            <FeeAndShareHeader key={`hdr-${loc.id}`} name={loc.name ?? 'Location'} />
          ))}
          <Th className="w-20" />
        </tr>
      </thead>
      <tbody>
        {fees.map((fee, i) => {
          const inEdit = Boolean(editingFee?.id && editingFee.id === fee.id);
          if (inEdit && editingFee) {
            return (
              <ProviderFeeEditRow
                key={fee.id}
                providerId={providerId}
                locations={locations}
                editing={editingFee}
                onDone={onDoneEditing}
                showLabels
              />
            );
          }
          return (
            <FeeRow
              key={fee.id}
              fee={fee}
              locations={locations}
              zebra={i % 2 === 0}
              isSelected={Boolean(fee.id && selected.has(fee.id))}
              onToggle={() => fee.id && onToggleRow(fee.id)}
              onArchive={() => fee.id && onArchive(fee.id)}
              onUnarchive={() => fee.id && onUnarchive(fee.id)}
              onEdit={() => onEdit(fee)}
              isMutating={isMutating}
            />
          );
        })}
      </tbody>
    </table>
  );
}

function FeeAndShareHeader({ name }: { name: string }) {
  return (
    <Fragment>
      <Th className="w-24 text-right">{name}, AED</Th>
      <Th className="w-40">{name}, share</Th>
    </Fragment>
  );
}

interface RowProps {
  fee: ProviderServiceFee;
  locations: OrgLocation[];
  zebra: boolean;
  isSelected: boolean;
  isMutating: boolean;
  onToggle: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
  onEdit: () => void;
}

function FeeRow({
  fee,
  locations,
  zebra,
  isSelected,
  isMutating,
  onToggle,
  onArchive,
  onUnarchive,
  onEdit,
}: RowProps) {
  const isArchived = Boolean(fee.isArchived);
  const serviceName =
    fee.procedureCode?.displayName ?? fee.shortDescription ?? 'Untitled service';
  const code = fee.procedureCode?.code;
  const system = fee.procedureCode?.system;

  const feesByLoc = useMemo(() => {
    const m = new Map<string, number | undefined>();
    fee.providerVisitFees?.forEach((v) => {
      if (v.location?.id) m.set(v.location.id, v.fee);
    });
    return m;
  }, [fee.providerVisitFees]);

  const sharesByLoc = useMemo(() => {
    const m = new Map<string, ProviderFeeShare>();
    fee.providerFeeShares?.forEach((s) => {
      if (s.location?.id) m.set(s.location.id, s);
    });
    return m;
  }, [fee.providerFeeShares]);

  return (
    <tr
      className={cn(
        'border-b border-border-subtle',
        isArchived && 'opacity-60',
        zebra ? 'bg-surface-card' : 'bg-grey-100',
      )}
    >
      <Td>
        <Checkbox
          checked={isSelected}
          onCheckedChange={onToggle}
          aria-label={`Select ${serviceName}`}
        />
      </Td>
      <Td>
        <div className="flex flex-col">
          <span className="font-medium text-brand-depth truncate" title={serviceName}>
            {serviceName}
          </span>
          {code && (
            <span className="text-xs text-text-muted">
              {code}
              {system ? ` · ${system}` : ''}
            </span>
          )}
        </div>
      </Td>
      <Td className="text-right tabular-nums align-top">
        {fee.wTax != null ? fee.wTax.toFixed(2).replace(/\.00$/, '') : '0'}
      </Td>
      {locations.map((loc) => {
        const val = loc.id ? feesByLoc.get(loc.id) : undefined;
        const share = loc.id ? sharesByLoc.get(loc.id) : undefined;
        return (
          <Fragment key={loc.id}>
            <Td className="text-right tabular-nums align-top">
              {val != null ? formatTerse(val) : <Dash />}
            </Td>
            <Td className="align-top">{share ? <ShareCell share={share} /> : <Dash />}</Td>
          </Fragment>
        );
      })}
      <Td className="align-top">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onEdit}
            disabled={isArchived}
            aria-label={`Edit ${serviceName}`}
            className="icon-action"
          >
            <Pencil className="size-4" aria-label="Edit" />
          </button>
          {isArchived ? (
            <button
              type="button"
              onClick={onUnarchive}
              disabled={isMutating}
              aria-label={`Unarchive ${serviceName}`}
              className="icon-action"
            >
              <ChevronLeft className="size-4" aria-label="Unarchive" />
            </button>
          ) : (
            <button
              type="button"
              onClick={onArchive}
              disabled={isMutating}
              aria-label={`Archive ${serviceName}`}
              className="icon-action"
            >
              <Trash2 className="size-4" aria-label="Archive" />
            </button>
          )}
        </div>
      </Td>
    </tr>
  );
}

/**
 * Multi-line share display — mirrors Flutter `provider_fees_list_widget.dart`:
 *   Cash: 70% / Insurance: 70% / Corporate: 70%
 * Fixed mode collapses to one line: `Fixed: NNN`.
 */
function ShareCell({ share }: { share: ProviderFeeShare }) {
  if (!share.isPercent) {
    return (
      <span className="text-sm text-text-secondary tabular-nums">
        Fixed: {share.fixed != null ? formatTerse(share.fixed) : '0'}
      </span>
    );
  }
  const fmt = (n: number | undefined) => `${n ?? 0}%`;
  return (
    <div className="flex flex-col gap-1 text-sm text-text-secondary">
      <span>Cash: {fmt(share.percent?.cash)}</span>
      <span>Insurance: {fmt(share.percent?.insurance)}</span>
      <span>Corporate: {fmt(share.percent?.corporate)}</span>
    </div>
  );
}

function Dash() {
  return <span className="text-text-hint">—</span>;
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <th
      className={cn(
        'text-left font-medium py-2 px-3 border-b border-border-default whitespace-nowrap bg-grey-100 sticky top-0 z-10',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <td className={cn('py-3 px-3', className)}>{children}</td>;
}


export default ProviderFeesDetailPage;
